import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../i18n";
import StarRating from "../components/StarRating";
import { Skull, Disc3 } from "lucide-react";

export default function PublicShare() {
  const { token } = useParams();
  const { t, lang, toggle } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/share/${token}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-background text-zinc-400 label-caps">404 — {t("public_collection")}</div>
  );
  if (!data) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Skull className="text-primary" size={24} />
            <span className="heading text-xl font-bold">MetalVault</span>
            <span className="label-caps text-xs text-zinc-500 ml-2 hidden sm:inline">{t("public_collection")}</span>
          </div>
          <button onClick={toggle} className="text-xs label-caps text-zinc-400 hover:text-white">{lang.toUpperCase()}</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h1 className="heading text-4xl font-bold text-white mb-1">{data.owner}</h1>
        <p className="text-zinc-500 text-sm mb-8">{data.bands.length} {t("bands_count")} · {t("powered_by")}</p>

        {data.top_seen_bands?.length > 0 && (
          <div className="mb-10 bg-zinc-900 border border-zinc-800 rounded-sm p-5 max-w-xl">
            <h2 className="label-caps text-sm text-zinc-400 mb-4">{t("top_seen")}</h2>
            <div className="space-y-2">
              {data.top_seen_bands.map((b, i) => (
                <div key={b.id} data-testid="public-top-seen" className="flex items-center justify-between bg-black border border-zinc-800 rounded-sm px-3 py-2">
                  <span className="flex items-center gap-3">
                    <span className="label-caps text-xs text-zinc-500 w-5 text-right">{i + 1}</span>
                    <span className="text-white text-sm">{b.flag} {b.name}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-primary font-semibold">
                    <Disc3 size={14} /> {b.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.bands.map((b) => (
            <div key={b.id} data-testid="public-band-card" className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
              <div className="relative h-44 bg-zinc-950 flex items-center justify-center">
                {b.logo_url ? <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover opacity-90" /> : <Skull size={48} className="text-zinc-700" />}
                <span className="absolute top-2 right-2 text-xs label-caps px-2 py-1 bg-black/70 border border-white/10 text-primary rounded-sm">{b.category}</span>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  {b.flag && <span className="text-lg">{b.flag}</span>}
                  <h3 className="heading text-lg font-semibold text-white truncate">{b.name}</h3>
                </div>
                <p className="text-sm text-zinc-400 truncate">{b.genre}{b.subgenre ? ` · ${b.subgenre}` : ""}</p>
                <StarRating value={b.rating || 0} size={16} testid={`public-rating-${b.id}`} />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
